/**
 * Visitor and Session ID generation for analytics.
 * Follows the common web-analytics pattern: persistent visitor ID (~2yr expiry), session-scoped session ID (30min idle).
 */

/** UUID v4 using browser crypto — no external dependency. */
function uuidv4(): string {
  return crypto.randomUUID();
}

const VISITOR_ID_KEY = 'tilly_visitor_id';
const VISITOR_ID_EXPIRY_KEY = 'tilly_visitor_id_expiry';
const SESSION_ID_KEY = 'tilly_session_id';
const SESSION_LAST_ACTIVITY_KEY = 'tilly_session_last_activity';
const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const VISITOR_ID_LIFETIME_MS = 2 * 365 * 24 * 60 * 60 * 1000; // 2 years

interface VisitorSessionIds {
  visitor_id: string;
  session_id: string;
  is_new_visitor: boolean;
}

/**
 * Get or create a persistent visitor ID (stored in localStorage).
 * Returns a flag indicating if this is a brand-new visitor (no prior visitor ID).
 */
function getOrCreateVisitorId(): { id: string; is_new: boolean } {
  const stored = localStorage.getItem(VISITOR_ID_KEY);
  const expiryStr = localStorage.getItem(VISITOR_ID_EXPIRY_KEY);
  const now = Date.now();

  // Check if stored visitor ID exists and hasn't expired
  if (stored && expiryStr) {
    const expiry = parseInt(expiryStr, 10);
    if (!Number.isNaN(expiry) && expiry > now) {
      return { id: stored, is_new: false };
    }
  }

  // Create new visitor ID
  const newId = uuidv4();
  const expiry = now + VISITOR_ID_LIFETIME_MS;
  localStorage.setItem(VISITOR_ID_KEY, newId);
  localStorage.setItem(VISITOR_ID_EXPIRY_KEY, expiry.toString());

  return { id: newId, is_new: true };
}

/**
 * Get or create a session ID (stored in sessionStorage).
 * Session expires after SESSION_IDLE_TIMEOUT_MS of inactivity.
 */
function getOrCreateSessionId(): string {
  const stored = sessionStorage.getItem(SESSION_ID_KEY);
  const lastActivityStr = sessionStorage.getItem(SESSION_LAST_ACTIVITY_KEY);
  const now = Date.now();

  // Check if stored session ID exists and hasn't timed out
  if (stored && lastActivityStr) {
    const lastActivity = parseInt(lastActivityStr, 10);
    if (!Number.isNaN(lastActivity) && now - lastActivity < SESSION_IDLE_TIMEOUT_MS) {
      // Session still active; update last activity time
      sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, now.toString());
      return stored;
    }
  }

  // Create new session ID
  const newId = uuidv4();
  sessionStorage.setItem(SESSION_ID_KEY, newId);
  sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, now.toString());

  return newId;
}

/**
 * Get current visitor and session IDs. Implicitly creates them if missing.
 */
export function getVisitorSessionIds(): VisitorSessionIds {
  const { id: visitorId, is_new } = getOrCreateVisitorId();
  const sessionId = getOrCreateSessionId();

  return {
    visitor_id: visitorId,
    session_id: sessionId,
    is_new_visitor: is_new,
  };
}

/**
 * Refresh the session's last-activity timestamp (called on user interaction).
 */
export function touchSession(): void {
  const now = Date.now();
  sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, now.toString());
}
